import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { KeyRound, Mail, ArrowLeft, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PasswordResetPage() {
  const [step, setStep] = useState(1); // 1: email, 2: code + new password, 3: success
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleRequestCode = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Bitte E-Mail-Adresse eingeben");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/members/request-password-reset?email=${encodeURIComponent(email)}`);
      toast.success("Reset-Code wurde gesendet (falls ein Konto existiert)");
      setStep(2);
    } catch (err) {
      toast.error("Fehler beim Senden des Reset-Codes");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!code || !newPassword || !confirmPassword) {
      toast.error("Bitte alle Felder ausfüllen");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwörter stimmen nicht überein");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Passwort muss mindestens 6 Zeichen lang sein");
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `${API}/members/reset-password?email=${encodeURIComponent(email)}&code=${encodeURIComponent(code)}&new_password=${encodeURIComponent(newPassword)}`
      );
      toast.success("Passwort wurde erfolgreich geändert!");
      setStep(3);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Zurücksetzen des Passworts");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="bg-card border border-white/10 p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <KeyRound className="w-12 h-12 mx-auto text-primary mb-4" />
            <h1 className="text-2xl font-bold uppercase tracking-tight" style={{ fontFamily: 'Oswald' }}>
              Passwort zurücksetzen
            </h1>
          </div>

          {/* Step 1: Enter Email */}
          {step === 1 && (
            <form onSubmit={handleRequestCode} className="space-y-6">
              <p className="text-sm text-muted-foreground text-center">
                Geben Sie Ihre E-Mail-Adresse ein. Wir senden Ihnen einen Code zum Zurücksetzen Ihres Passworts.
              </p>
              <div>
                <Label htmlFor="email">E-Mail-Adresse</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="ihre@email.at"
                    className="pl-10 bg-input/50 border-white/10 rounded-none"
                    data-testid="reset-email"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-primary text-primary-foreground font-bold uppercase tracking-wider text-sm disabled:opacity-50"
                data-testid="reset-request-btn"
              >
                {loading ? "Wird gesendet..." : "Code anfordern"}
              </button>
              <Link to="/mitglied-login" className="flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground">
                <ArrowLeft className="w-4 h-4" />
                Zurück zur Anmeldung
              </Link>
            </form>
          )}

          {/* Step 2: Enter Code and New Password */}
          {step === 2 && (
            <form onSubmit={handleResetPassword} className="space-y-6">
              <p className="text-sm text-muted-foreground text-center">
                Wir haben einen 6-stelligen Code an <strong>{email}</strong> gesendet.
              </p>
              <div>
                <Label htmlFor="code">Reset-Code</Label>
                <Input
                  id="code"
                  type="text"
                  value={code}
                  onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="123456"
                  className="mt-1 bg-input/50 border-white/10 rounded-none text-center text-2xl tracking-widest"
                  maxLength={6}
                  data-testid="reset-code"
                />
              </div>
              <div>
                <Label htmlFor="newPassword">Neues Passwort</Label>
                <Input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder="••••••••"
                  className="mt-1 bg-input/50 border-white/10 rounded-none"
                  data-testid="reset-new-password"
                />
              </div>
              <div>
                <Label htmlFor="confirmPassword">Passwort bestätigen</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  className="mt-1 bg-input/50 border-white/10 rounded-none"
                  data-testid="reset-confirm-password"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-primary text-primary-foreground font-bold uppercase tracking-wider text-sm disabled:opacity-50"
                data-testid="reset-submit-btn"
              >
                {loading ? "Wird gespeichert..." : "Passwort ändern"}
              </button>
              <button
                type="button"
                onClick={() => setStep(1)}
                className="w-full text-sm text-muted-foreground hover:text-foreground"
              >
                Code erneut anfordern
              </button>
            </form>
          )}

          {/* Step 3: Success */}
          {step === 3 && (
            <div className="text-center space-y-6">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto">
                <Check className="w-8 h-8 text-green-500" />
              </div>
              <div>
                <h2 className="text-xl font-bold mb-2">Passwort geändert!</h2>
                <p className="text-sm text-muted-foreground">
                  Ihr Passwort wurde erfolgreich geändert. Sie können sich jetzt anmelden.
                </p>
              </div>
              <button
                onClick={() => navigate("/mitglied-login")}
                className="w-full py-3 bg-primary text-primary-foreground font-bold uppercase tracking-wider text-sm"
                data-testid="reset-to-login-btn"
              >
                Zur Anmeldung
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
